import { type JanusCtx, type Row, activeStatuses, ecId, handBack, isInventoryRow, itemField, record, upper } from './context.js';
import { runTriggerFamily, TAKES_ANY_DAMAGE, type TriggerGapFn } from './trigger-executor.js';
import { recompileMemberStatusDiff } from '../compile/member-status-diff.js';
import { applyMemberStatusRoots, type MemberStatusStep } from '../compile/member-status-recompile.js';
import { readStatusRowStack } from '../compile/status-stack-reader.js';
import { statusCasterId, statusItemId } from '../statuses/expiry.js';

/** A positive incoming hit is known. Reuse the damage-taken family for mitigation reactions (Minor Defiance,
 * Thick Hide); no catalog re-key is needed. Other listeners retain their post-damage timing.
 * Consent pauses the existing caller's resumable dice tape before any vitality application. */
export function runPreMitigationReactions(ctx: JanusCtx, attacker: Row, target: Row, damageTypes: string[], gap?: TriggerGapFn): void {
  if (isInventoryRow(target)) return;
  const statuses = () => activeStatuses(ctx, target).map(status => {
    const id = upper(itemField(status, 'status_id'));
    return { id, item: statusItemId(status), caster: upper(itemField(status, 'caster')) || statusCasterId(status),
      stack: ctx.statusStacks?.find(change => upper(change.status_id) === id)?.status_stack ?? readStatusRowStack(status) };
  });
  const before = statuses();
  const inciting = ecId(attacker) ? ctx.members.get(ecId(attacker)) ?? null : null;
  runTriggerFamily(ctx, { trigger: TAKES_ANY_DAMAGE, damagePhase: 'before', damageTypes }, target, inciting, [target], false,
    gap, isInventoryRow(attacker));
  const after = statuses();
  const same = (left: typeof before[number], right: typeof before[number]): boolean => left.id === right.id
    && left.item === right.item && left.stack === right.stack;
  const steps: MemberStatusStep[] = before.filter(status => !after.some(other => same(status, other))).map(status => ({
    kind: 'remove', statusId: status.id, statusItemId: status.item }));
  steps.push(...after.filter(status => !before.some(other => same(status, other))).map(status => ({
    kind: 'add' as const, statusId: status.id, statusItemId: status.item, casterId: status.caster, stack: status.stack })));
  if (!steps.length) return;
  // Update only the changed status clusters; pending rows remain owned by ctx and are never installed twice.
  const projected = recompileMemberStatusDiff(target, steps, { definition: ctx.read.definition,
    catalog: record(target.catalog), roster: () => [...ctx.members.values()] });
  if (!projected || !projected.ok) handBack('pre-mitigation:status-diff:' + (projected?.reason ?? 'unsupported'));
  const computed = (target.computed ??= {}) as Row;
  computed.creature = projected.creature;
  computed.vitality = projected.vitality;
  applyMemberStatusRoots(target, projected);
}
